import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import { trackPreviewGenerated, trackPreviewEmailCaptured, trackPreviewSignupClick } from '../utils/analytics';

// Interactive landing-page story preview with a value-first email gate:
//   1. Visitor picks a child's name + age + interests (no signup)
//   2. Sees the story opening instantly — ending blurred
//   3. Unlocks the full story with their email (lead captured for drip)
// The backend never sends the ending until the email is provided.

const INTERESTS = [
  { value: 'magic',     label: 'Magic',     emoji: '✨' },
  { value: 'dragons',   label: 'Dragons',   emoji: '🐉' },
  { value: 'space',     label: 'Space',     emoji: '🚀' },
  { value: 'animals',   label: 'Animals',   emoji: '🐾' },
  { value: 'ocean',     label: 'Ocean',     emoji: '🌊' },
  { value: 'adventure', label: 'Adventure', emoji: '🗺️' },
];

// Optional props let SEO landing pages pre-fill the widget with the page's
// context (e.g. the child's name they searched, a matching interest) so a
// visitor experiences a real personalised story before any signup wall.
const StoryPreviewWidget = ({ initialName = '', initialAge = '5', initialInterests = [], heading, subheading }) => {
  const [name, setName] = useState(initialName);
  const [age, setAge] = useState(String(initialAge || '5'));
  const [interests, setInterests] = useState(Array.isArray(initialInterests) ? initialInterests : []);
  const [story, setStory] = useState(null);        // gated story (preview)
  const [fullStory, setFullStory] = useState(null); // unlocked story (post-email)
  const [previewId, setPreviewId] = useState(null); // server-side id of the generated story
  const [claimSecret, setClaimSecret] = useState(null); // proves we created this preview
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [signupRequired, setSignupRequired] = useState(false); // email already used its free story
  const resultRef = useRef(null);

  const toggleInterest = (value) => {
    setInterests(prev =>
      prev.includes(value)
        ? prev.filter(i => i !== value)
        : prev.length >= 3 ? prev : [...prev, value]
    );
  };

  const scrollToWidget = () => {
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || loading) return;
    setError('');
    setLoading(true);
    try {
      const body = { name: name.trim(), age: Number(age), interests };
      // Minimum 900ms so the "magic" feels like it's really being written
      const [res] = await Promise.all([
        API.post('/stories/preview', body),
        new Promise(r => setTimeout(r, 900)),
      ]);
      setStory(res.data.story);
      setPreviewId(res.data.previewId);
      setClaimSecret(res.data.claimSecret || null);
      trackPreviewGenerated(Number(age));
      scrollToWidget();
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email.trim() || unlocking) return;
    setUnlockError('');
    setUnlocking(true);
    try {
      const [res] = await Promise.all([
        API.post('/stories/preview/unlock', { previewId, claimSecret, email: email.trim() }),
        new Promise(r => setTimeout(r, 700)),
      ]);
      setFullStory(res.data.story);
      setEmailSent(!!res.data.emailSent);
      trackPreviewEmailCaptured(story.age);
      scrollToWidget();
    } catch (err) {
      if (err.response?.data?.signupRequired) {
        setSignupRequired(true);
        setUnlockError('');
      } else {
        setUnlockError(err.response?.data?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setUnlocking(false);
    }
  };

  const handleReset = () => {
    setStory(null);
    setFullStory(null);
    setPreviewId(null);
    setName('');
    setInterests([]);
    setEmail('');
    setEmailSent(false);
    setError('');
    setUnlockError('');
    setSignupRequired(false);
  };

  const active = fullStory || story;

  return (
    <section className="py-5 bg-dream text-white" ref={resultRef} style={{scrollMarginTop:'80px'}}>
      <div className="container py-3">
        <div className="text-center mb-4">
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill" style={{fontSize:'13px'}}>
            ✨ Try it now — free, no signup needed
          </span>
          <h2 className="fw-bold mb-2" style={{fontFamily:"'Fredoka One',cursive", fontSize:'clamp(28px,4vw,40px)'}}>
            {heading || 'Watch a story come to life'}
          </h2>
          <p style={{color:'#c9a8ff', fontSize:'16px', maxWidth:'520px', margin:'0 auto'}}>
            {subheading || "Type your child's name, pick what they love, and see the kind of story they'll fall asleep to tonight."}
          </p>
        </div>

        <div className="mx-auto" style={{maxWidth:'720px'}}>
          {!active ? (
            /* ── Step 1: name + age + interests ──────────────────── */
            <div className="card border-0 shadow-lg p-4" style={{borderRadius:'24px', background:'rgba(255,255,255,0.1)', backdropFilter:'blur(16px)', border:'1px solid rgba(255,255,255,0.2)'}}>
              {error && (
                <div className="alert alert-danger py-2 small rounded-3 d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-exclamation-circle-fill"></i>{error}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label fw-semibold small" style={{color:'rgba(255,255,255,0.85)'}}>Your child's name</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="e.g. Emma"
                      maxLength={30}
                      style={{borderRadius:'12px'}}
                      value={name}
                      onChange={e => setName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small" style={{color:'rgba(255,255,255,0.85)'}}>Age</label>
                    <select
                      className="form-select form-select-lg"
                      style={{borderRadius:'12px'}}
                      value={age}
                      onChange={e => setAge(e.target.value)}
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(a => (
                        <option key={a} value={a}>{a} year{a > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="form-label fw-semibold small mb-2" style={{color:'rgba(255,255,255,0.85)'}}>
                    What do they love? <span style={{fontWeight:400, color:'rgba(255,255,255,0.55)'}}>(pick up to 3 — optional)</span>
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {INTERESTS.map(({ value, label, emoji }) => {
                      const selected = interests.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          className="btn btn-sm rounded-pill px-3 py-2 fw-semibold"
                          style={selected
                            ? {background:'#f9c74f', color:'#2d1b69', border:'1px solid #f9c74f', fontSize:'14px'}
                            : {background:'rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.9)', border:'1px solid rgba(255,255,255,0.25)', fontSize:'14px'}}
                          onClick={() => toggleInterest(value)}
                        >
                          {emoji} {label}{selected && <i className="bi bi-check-lg ms-1"></i>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button type="submit" className="btn btn-warning btn-lg w-100 rounded-pill fw-bold mt-4" style={{fontSize:'16px'}} disabled={loading || !name.trim()}>
                  {loading
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Writing {name.trim() || 'your child'}'s story…</>
                    : <>✨ Create My Story</>}
                </button>
              </form>
              <div className="text-center mt-3" style={{color:'rgba(255,255,255,0.55)', fontSize:'12px'}}>
                <i className="bi bi-shield-check me-1"></i>Instant preview · No email or signup required
              </div>
            </div>
          ) : (
            /* ── Steps 2 & 3: the story (gated → unlocked) ───────── */
            <>
              <div className="card border-0 shadow-lg p-4 p-md-5" style={{borderRadius:'24px', background:'var(--card-bg)'}}>
                <div className="text-center mb-4">
                  <div style={{fontSize:'44px', marginBottom:'8px'}}>{active.emoji}</div>
                  <h3 className="fw-bold mb-2" style={{fontFamily:"'Fredoka One',cursive", color:'var(--text-main)', fontSize:'clamp(22px,3vw,30px)'}}>
                    {active.title}
                  </h3>
                  <div style={{color:'var(--text-soft)', fontSize:'13px'}}>
                    📖 {active.readingTime} min read · ✨ {active.theme} · Made for {active.childName}, age {active.age}
                  </div>
                </div>

                {fullStory ? (
                  /* ── Unlocked: the whole story ───────────────────── */
                  <>
                    {emailSent && (
                      <div className="d-flex align-items-center gap-2 p-3 rounded-3 mb-4" style={{background:'var(--green-section)', border:'1px solid var(--border-green)'}}>
                        <span style={{fontSize:'20px'}}>📬</span>
                        <span style={{color:'var(--text-green)', fontSize:'14px', fontWeight:600}}>
                          We've also sent this story to your inbox, so you can read it tonight.
                        </span>
                      </div>
                    )}

                    {fullStory.paragraphs.map((p, i) => (
                      <p key={i} style={{color:'var(--text-body)', fontSize:'16px', lineHeight:1.9, marginBottom:'18px'}}>
                        {p}
                      </p>
                    ))}

                    <div className="text-center pt-3" style={{borderTop:'1px dashed var(--border-purple)'}}>
                      <div style={{fontSize:'30px', marginBottom:'8px'}}>🌙</div>
                      <div className="fw-bold mb-1" style={{color:'var(--text-main)', fontSize:'18px', fontFamily:"'Fredoka One',cursive"}}>
                        {fullStory.childName} deserves a new story every night
                      </div>
                      <p style={{color:'var(--text-muted2)', fontSize:'14px', maxWidth:'440px', margin:'0 auto 18px'}}>
                        Get 5 free personalised stories every month — with magical illustrations, narration, and {fullStory.childName} as the hero.
                      </p>
                      <Link
                        to={`/register?email=${encodeURIComponent(email.trim())}`}
                        className="btn btn-warning btn-lg px-5 rounded-pill fw-bold"
                        style={{fontSize:'16px'}}
                        onClick={() => trackPreviewSignupClick()}
                      >
                        Create My Free Account
                      </Link>
                      <div className="mt-2" style={{color:'var(--text-soft)', fontSize:'12px'}}>
                        No credit card required · Takes 30 seconds
                      </div>
                    </div>
                  </>
                ) : (
                  /* ── Gated: opening + blurred teaser + email gate ── */
                  <>
                    {story.visibleParagraphs.map((p, i) => (
                      <p key={i} style={{color:'var(--text-body)', fontSize:'16px', lineHeight:1.9, marginBottom:'18px'}}>
                        {p}
                      </p>
                    ))}

                    {/* Blurred teaser — the ending never leaves the server */}
                    <div style={{position:'relative', marginBottom:'8px'}}>
                      <p aria-hidden="true" style={{color:'var(--text-body)', fontSize:'16px', lineHeight:1.9, filter:'blur(6px)', userSelect:'none', pointerEvents:'none', margin:0}}>
                        {story.teaserParagraph}
                      </p>
                      <div style={{position:'absolute', inset:0, background:'linear-gradient(to bottom, transparent 0%, var(--card-bg) 90%)'}}></div>
                    </div>

                    <div className="text-center pt-3" style={{borderTop:'1px dashed var(--border-purple)'}}>
                      <div style={{fontSize:'30px', marginBottom:'8px'}}>🔒</div>
                      <div className="fw-bold mb-1" style={{color:'var(--text-main)', fontSize:'18px', fontFamily:"'Fredoka One',cursive"}}>
                        How does {story.childName}'s adventure end?
                      </div>
                      <p style={{color:'var(--text-muted2)', fontSize:'14px', maxWidth:'440px', margin:'0 auto 16px'}}>
                        Enter your email to read the full story — we'll send a copy to your inbox too, plus you get 5 free personalised stories every month.
                      </p>

                      {unlockError && (
                        <div className="alert alert-danger py-2 small rounded-3 d-inline-flex align-items-center gap-2 mb-3">
                          <i className="bi bi-exclamation-circle-fill"></i>{unlockError}
                        </div>
                      )}

                      {signupRequired ? (
                        /* This email already used its one free story */
                        <div className="mx-auto" style={{maxWidth:'440px'}}>
                          <div className="p-3 rounded-3 mb-3" style={{background:'var(--purple-section)', border:'1px solid var(--border-purple)'}}>
                            <span style={{color:'var(--text-main)', fontSize:'14px', fontWeight:600}}>
                              ✨ You've already unlocked your free story with this email.
                            </span>
                            <div style={{color:'var(--text-muted2)', fontSize:'13px', marginTop:'4px'}}>
                              Create a free account to get 5 new personalised stories every month.
                            </div>
                          </div>
                          <Link
                            to={`/register?email=${encodeURIComponent(email.trim())}`}
                            className="btn btn-warning btn-lg px-5 rounded-pill fw-bold"
                            style={{fontSize:'16px'}}
                            onClick={() => trackPreviewSignupClick()}
                          >
                            Create My Free Account
                          </Link>
                          <div className="mt-2">
                            <button type="button" className="btn btn-link btn-sm text-decoration-none" style={{color:'var(--text-soft)', fontSize:'13px'}}
                              onClick={() => { setSignupRequired(false); setEmail(''); }}>
                              Use a different email
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleUnlock} className="mx-auto" style={{maxWidth:'440px'}}>
                            <div className="d-flex flex-column flex-sm-row gap-2">
                              <input
                                type="email"
                                className="form-control form-control-lg"
                                placeholder="you@example.com"
                                style={{borderRadius:'50px', fontSize:'15px', textAlign:'center'}}
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                              />
                              <button type="submit" className="btn btn-warning btn-lg rounded-pill fw-bold flex-shrink-0 px-4" style={{fontSize:'15px'}} disabled={unlocking || !email.trim()}>
                                {unlocking
                                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Unlocking…</>
                                  : <>🔓 Unlock the Story</>}
                              </button>
                            </div>
                          </form>

                          <div className="mt-2" style={{color:'var(--text-soft)', fontSize:'12px'}}>
                            Free forever · No credit card · Unsubscribe anytime
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="text-center mt-3">
                <button type="button" className="btn btn-link text-decoration-none" style={{color:'#c9a8ff', fontSize:'14px'}} onClick={handleReset}>
                  <i className="bi bi-arrow-repeat me-1"></i>Try another name
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default StoryPreviewWidget;
